import {
  Component,
  ChangeDetectionStrategy,
  OnInit,
  ChangeDetectorRef,
} from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import {
  passwordEqualValidation,
  passwordValidation,
} from '@shared/validations';
import { ActivatedRoute, Router } from '@angular/router';
import { DialogService } from '@migo-apps/dialogs';
import { AuthenticationService } from '../../services/authentication.service';
import { finalize, Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '@migo-apps/components';
import { countdown, Countdown } from '@shared/utils';
import { BreakpointService } from '../../../shared/services/breakpoint/breakpoint.service';

const SOMETHING_WRONG_TITLE = 'COMMON.SOMETHING-WRONG-TITLE';
const ACTIVATE_ACCOUNT_WRONG = 'AUTH.DIALOG.NEW-PASS.ACTIVATE-ACCOUNT-WRONG';
const RESET_PASS_WRONG = 'AUTH.DIALOG.NEW-PASS.RESET-PASS-WRONG';
const TRY_LATER = 'AUTH.DIALOG.NEW-PASS.TRY-LATER';
const RETRY = 'AUTH.DIALOG.NEW-PASS.RETRY';
const COULD_NOT = 'AUTH.DIALOG.NEW-PASS.COULD-NOT';

/**
 *Pagina para establecer una nueva contraseña
 *
 * @export
 * @class NewPasswordPage
 * @implements {OnInit}
 */
@Component({
  standalone: false,
  selector: 'new-password-page',
  templateUrl: './new-password.page.html',
  styleUrls: ['./new-password.page.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NewPasswordPage implements OnInit {
  private email!: string;
  private otp!: string;

  loading = false;
  newPasswordForm!: FormGroup;

  activateAccount = false;

  hasCapitalLetter: boolean | undefined;
  hasLowercase: boolean | undefined;
  hasNumber: boolean | undefined;
  hasSymbol: boolean | undefined;
  hasLength: boolean | undefined;

  changeSucces = false;
  countdown!: Countdown;

  constructor(
    private builder: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private dialogSvc: DialogService,
    private authSvc: AuthenticationService,
    private cd: ChangeDetectorRef,
    private i18n: TranslateService,
    private toastService: ToastService,
    public bp: BreakpointService
  ) {
    this.createForm();
    this.onPasswordValueChanges();
  }

  /**
   *Se obtienen los queryParams de la ruta actual
   *
   * @memberof NewPasswordPage
   */
  ngOnInit(): void {
    this.route.queryParams.subscribe(({ email, otp }) => {
      this.email = email;
      this.otp = otp;
      this.activateAccount = this.router.url.includes('activate-account');
    });

    this.cd.markForCheck();
  }

  /**
   *Envia la solicitud para establecer una nueva contraseña,
   *necesita el email, el codigo otp y la nueva contraseña
   * @return {*}
   * @memberof NewPasswordPage
   */
  onSubmit() {
    if (this.newPasswordForm.invalid) {
      this.toastService.toast(
        'Debes de cumplir todos los requisitos<br />para continuar',
        'error'
      );
      return;
    }

    this.loading = true;

    let request$: Observable<unknown>;

    if (this.activateAccount) {
      request$ = this.authSvc.activateAccount(this.password?.value);
    } else {
      request$ = this.authSvc.resetPassword(
        this.email,
        this.otp,
        this.password?.value
      );
    }

    request$
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cd.markForCheck();
        })
      )
      .subscribe({
        next: () => {
          this.changeSucces = true;
          this.toastService.toast(
            'Tu contraseña se actualizó<br />correctamente',
            'success'
          );
          this.showCountdown(1);
        },
        error: () => {
          this.i18n
            .get([
              SOMETHING_WRONG_TITLE,
              COULD_NOT,
              ACTIVATE_ACCOUNT_WRONG,
              RESET_PASS_WRONG,
              TRY_LATER,
              RETRY,
            ])
            .subscribe((value) => {
              this.dialogSvc.openInformationDialog({
                title: value[SOMETHING_WRONG_TITLE],
                message: `${value[COULD_NOT]} ${
                  this.activateAccount
                    ? value[ACTIVATE_ACCOUNT_WRONG]
                    : value[RESET_PASS_WRONG]
                }, ${value[TRY_LATER]}`,
                buttonText: value[RETRY],
              });
            });
        },
      });
  }

  /**
   * We create a form group with two controls, password and confirm. The password
   * control has a custom validator, passwordValidation, and the confirm control has
   * a required validator. The form group has a custom validator,
   * passwordEqualValidation, which compares the password and confirm controls
   */
  private createForm(): void {
    this.newPasswordForm = this.builder.group(
      {
        password: ['', [Validators.required, passwordValidation]],
        confirm: ['', Validators.required],
      },
      { validators: [passwordEqualValidation('password', 'confirm')] }
    );
  }

  onPasswordValueChanges(): void {
    this.newPasswordForm
      .get('password')
      ?.valueChanges.subscribe((password: string) => {
        this.evaluatePassword(password);
      });
  }

  evaluatePassword(password: string): void {
    this.hasCapitalLetter = /[A-Z]/.test(password);
    this.hasLowercase = /[a-z]/.test(password);
    this.hasNumber = /\d/.test(password);
    this.hasSymbol = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    this.hasLength = password.length >= 10;
  }

  get password() {
    return this.newPasswordForm.get('password');
  }
  get confirm() {
    return this.newPasswordForm.get('confirm');
  }

  /**
   *Crea una nueva subscripcion a una cuenta regresiva
   *
   * @private
   * @param {number} minutes
   * @memberof ValidateOtpPage
   */
  private showCountdown(minutes: number) {
    countdown(minutes, 10).subscribe((countdown) => {
      this.countdown = countdown;
      this.cd.markForCheck();
      if (this.countdown.finished) {
        this.router.navigate(['/']);
      }
    });
  }
}
